import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { pickLocalized, useLang, useT } from '../../i18n';
import { api } from '../../lib/api';
import type {
  MochiConversationIndex,
  MochiConversationWeek,
} from '../../types/mochiEnglish';

type LoadState = 'loading' | 'ready' | 'error';
const STORAGE_KEY = 'botianhome.mochi.conversation-week';

export default function MochiConversations() {
  const t = useT();
  const { lang } = useLang();
  const [state, setState] = useState<LoadState>('loading');
  const [detailState, setDetailState] = useState<LoadState>('loading');
  const [index, setIndex] = useState<MochiConversationIndex | null>(null);
  const [week, setWeek] = useState<MochiConversationWeek | null>(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.mochiEnglish.conversations()
      .then((response) => {
        if (cancelled) return;
        let nextWeek = response.suggested_week;
        try {
          const stored = Number.parseInt(localStorage.getItem(STORAGE_KEY) ?? '', 10);
          if (Number.isInteger(stored) && stored >= 1 && stored <= response.total_weeks) nextWeek = stored;
        } catch {
          // Private browsing may reject local storage; the server suggestion is enough.
        }
        setSelectedWeek(nextWeek);
        setIndex(response);
        setState('ready');
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : 'unknown error');
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (state !== 'ready') return;
    const controller = new AbortController();
    setDetailState('loading');
    api.mochiEnglish.conversationWeek(selectedWeek, controller.signal)
      .then((response) => {
        setWeek(response);
        setDetailState('ready');
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : 'unknown error');
        setDetailState('error');
      });
    return () => controller.abort();
  }, [selectedWeek, state]);

  const selectedSummary = useMemo(
    () => index?.weeks.find((candidate) => candidate.week === selectedWeek),
    [index, selectedWeek],
  );
  const selectedStage = useMemo(
    () => index?.stages.find((stage) => stage.id === selectedSummary?.stage_id),
    [index, selectedSummary],
  );

  function selectWeek(nextWeek: number) {
    if (!index || nextWeek < 1 || nextWeek > index.total_weeks) return;
    setSelectedWeek(nextWeek);
    try {
      localStorage.setItem(STORAGE_KEY, String(nextWeek));
    } catch {
      // Selection persistence is a convenience, not learning progress.
    }
  }

  if (state === 'loading') return <p className="font-mono text-sm text-coffee">{t('common.loading')}</p>;
  if (state === 'error' || !index) {
    return <p className="border-y border-rose-200 py-6 font-mono text-sm text-rose-700">{t('mochi.english.conversations.error', { error })}</p>;
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <section className="rounded-2xl border border-amber-200/70 bg-amber-50/60 p-5 md:p-6">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-amber-900/70">
          {t('mochi.english.conversations.weeklyOnly')}
        </p>
        <p className="mt-2 max-w-3xl leading-relaxed text-coffee">
          {pickLocalized(index.guidance, lang)}
        </p>
      </section>

      <section className="space-y-5">
        <div className="flex flex-col gap-4 border-b border-neutral-200 pb-5 md:flex-row md:items-end md:justify-between">
          <div>
            {selectedStage && (
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-coffee/70">
                {pickLocalized(selectedStage.title, lang)}
              </p>
            )}
            <h3 className="mt-2 text-3xl font-semibold text-cocoa">
              {t('mochi.english.conversations.week', { week: selectedWeek })}
              {selectedSummary ? ` · ${pickLocalized(selectedSummary.title, lang)}` : ''}
            </h3>
            {selectedSummary && (
              <p className="mt-2 max-w-2xl leading-relaxed text-coffee">
                {pickLocalized(selectedSummary.focus, lang)}
              </p>
            )}
          </div>

          <label className="flex min-w-0 flex-col gap-1 font-mono text-xs text-coffee">
            {t('mochi.english.conversations.chooseWeek')}
            <select
              value={selectedWeek}
              onChange={(event) => selectWeek(Number(event.target.value))}
              className="h-11 max-w-full rounded-lg border border-neutral-300 bg-white px-3 text-sm text-cocoa outline-none focus:border-cocoa md:w-72"
            >
              {index.weeks.map((candidate) => (
                <option key={candidate.week} value={candidate.week}>
                  {t('mochi.english.conversations.week', { week: candidate.week })} · {pickLocalized(candidate.title, lang)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => selectWeek(selectedWeek - 1)}
            disabled={selectedWeek === 1}
            className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm text-cocoa transition hover:border-cocoa disabled:cursor-not-allowed disabled:opacity-35"
          >
            {t('mochi.english.conversations.previous')}
          </button>
          <span className="font-mono text-xs text-coffee/70">
            {selectedWeek} / {index.total_weeks}
          </span>
          <button
            type="button"
            onClick={() => selectWeek(selectedWeek + 1)}
            disabled={selectedWeek === index.total_weeks}
            className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm text-cocoa transition hover:border-cocoa disabled:cursor-not-allowed disabled:opacity-35"
          >
            {t('mochi.english.conversations.next')}
          </button>
        </div>
      </section>

      {detailState === 'loading' && <p className="font-mono text-sm text-coffee">{t('common.loading')}</p>}
      {detailState === 'error' && (
        <p className="border-y border-rose-200 py-6 font-mono text-sm text-rose-700">
          {t('mochi.english.conversations.error', { error })}
        </p>
      )}
      {detailState === 'ready' && week && (
        <div className="grid gap-4 lg:grid-cols-3">
          {week.conversation_groups.map((group, groupIndex) => (
            <details
              key={group.id}
              className="group self-start overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-soft"
            >
              <summary className="cursor-pointer list-none p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cocoa/20">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.16em] text-coffee/60">
                      {t('mochi.english.conversations.group', { group: groupIndex + 1 })}
                    </p>
                    <h4 className="mt-2 text-xl font-semibold text-cocoa">{group.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-coffee">
                      {pickLocalized(group.talk_about, lang)}
                    </p>
                  </div>
                  <span aria-hidden className="mt-1 text-xl text-coffee transition group-open:rotate-45">+</span>
                </div>
              </summary>

              <div className="space-y-6 border-t border-neutral-100 px-5 pb-6 pt-5">
                <ConversationSection title={t('mochi.english.conversations.usefulPhrases')}>
                  <ul className="space-y-3">
                    {group.useful_phrases.map((line) => (
                      <li key={line.en}>
                        <p className="font-medium text-cocoa">“{line.en}”</p>
                        <p className="mt-0.5 text-sm text-coffee/75" lang="zh-CN">{line.zh}</p>
                      </li>
                    ))}
                  </ul>
                </ConversationSection>

                <ConversationSection title={t('mochi.english.conversations.flow')}>
                  <ol className="space-y-2 text-sm leading-relaxed text-coffee">
                    <li><strong className="text-cocoa">1.</strong> {pickLocalized(group.conversation_flow.notice, lang)}</li>
                    <li><strong className="text-cocoa">2.</strong> “{group.conversation_flow.say}”</li>
                    <li><strong className="text-cocoa">3.</strong> {pickLocalized(group.conversation_flow.wait, lang)}</li>
                    <li><strong className="text-cocoa">4.</strong> “{group.conversation_flow.answer}”</li>
                  </ol>
                </ConversationSection>

                <ConversationSection title={t('mochi.english.conversations.naturalMoments')}>
                  <ul className="space-y-3 text-sm text-coffee">
                    {group.natural_moments.map((moment) => (
                      <li key={`${moment.moment.en}-${moment.line}`}>
                        <span className="font-medium text-cocoa">{pickLocalized(moment.moment, lang)}:</span>{' '}
                        “{moment.line}”
                      </li>
                    ))}
                  </ul>
                </ConversationSection>

                <p className="border-l-2 border-emerald-500 pl-3 text-sm leading-relaxed text-coffee">
                  {pickLocalized(group.stop_cue, lang)}
                </p>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function ConversationSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h5 className="font-mono text-xs uppercase tracking-[0.14em] text-coffee/65">{title}</h5>
      <div className="mt-3">{children}</div>
    </section>
  );
}
